/**
 * Router paths configuration
 *
 * Centralized definition of all application routes.
 *
 * Pages a customer module serves (home, upload, files, master data, business
 * partners and deals, file submissions) live under that module's base path:
 * `""` for Rev EU, whose pages predate customer modules, and `/<slug>` for the
 * others (e.g. `/lasalle/files`). `routerPathsFor(base)` gives the whole table
 * for one base; `routerPaths` is the table for Rev EU.
 */

const copyFromQuery = (copyFromDealId?: string): string =>
    copyFromDealId ? `?copyFrom=${encodeURIComponent(copyFromDealId)}` : "";

/** Paths of the pages a customer module serves, under its base path. */
export const customerModulePaths = (base: string) => ({
    home: `${base}/home`,
    upload: {
        finJournals: `${base}/upload/fin-journals-standalone`,
        tbgl: `${base}/upload/tb-gl`,
        supporting: `${base}/upload/supporting`,
    },
    files: {
        // The all-files and TB/GL lists are superseded by the Homepage v2
        // board, but stay until the board is enabled everywhere (the
        // `homepage_v2_enabled` company flag) — with the flag off these are
        // still the landing pages.
        list: `${base}/files`,
        tbgl: `${base}/files/tb-gl`,
        finJournals: `${base}/files/fin-journals`,
        archive: `${base}/files/archive`,
        detail: (fileId: string) => `${base}/files/${fileId}`,
    },
    settings: {
        books: `${base}/settings/books`,
        chartOfAccounts: `${base}/settings/chart-of-accounts`,
        propcodes: `${base}/settings/propcodes`,
        segments: `${base}/settings/segments`,
        yardiImport: `${base}/settings/yardi-import`,
    },
    admin: {
        submissions: `${base}/admin/submissions`,
        businessPartners: `${base}/admin/business-partners`,
        businessPartnerDetails: (bpId: string) => `${base}/admin/business-partners/${bpId}/details`,
        businessPartnerDeals: (bpId: string) => `${base}/admin/business-partners/${bpId}/deals`,
        /** `copyFromDealId` opens the wizard prefilled from that deal (Copy deal). */
        businessPartnerDealCreate: (bpId: string, copyFromDealId?: string) =>
            `${base}/admin/business-partners/${bpId}/deals/new${copyFromQuery(copyFromDealId)}`,
        businessPartnerDealDetail: (bpId: string, dealId: string) =>
            `${base}/admin/business-partners/${bpId}/deals/${dealId}`,
        portfolioCompanies: `${base}/admin/portfolio-companies`,
        portfolioCompanyDealCreate: (pcId: string, copyFromDealId?: string) =>
            `${base}/admin/portfolio-companies/${pcId}/deals/new${copyFromQuery(copyFromDealId)}`,
    },
});

/** Every path, with a customer module's pages under `base`. */
export const routerPathsFor = (base: string) => {
    const pages = customerModulePaths(base);
    return {
        home: pages.home,
        // Landing page for a company whose customer module has no file pages
        // (or that has no customer module yet). Platform features only.
        welcome: "/welcome",
        upload: pages.upload,
        files: pages.files,
        settings: pages.settings,
        // Support
        support: {
            list: "/support",
            detail: (ticketId: string) => `/support/${ticketId}`,
        },
        // Admin routes
        admin: {
            ...pages.admin,
            auditLog: "/admin/audit-log",
            globalAuditLog: "/admin/global-audit-log",
            companies: "/admin/companies",
            supportForwarded: "/admin/support-forwarded",
            users: "/admin/users",
            announcements: "/admin/announcements",
            emailCommunications: "/admin/email-communications",
        },
    } as const;
};

export type TRouterPaths = ReturnType<typeof routerPathsFor>;

export const routerPaths: TRouterPaths = routerPathsFor("");

/**
 * The path of `pathname` inside the customer module mounted at `base`: the
 * module page it shows, as Rev EU would address it (`/lasalle/files/1` ->
 * `/files/1`). Paths outside the module are returned as they are.
 */
export const pathWithinCustomerModule = (pathname: string, base: string): string =>
    base && (pathname === base || pathname.startsWith(`${base}/`))
        ? pathname.slice(base.length) || "/"
        : pathname;
